import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi'

// Layout compartido para páginas pilar SEO (control-horario-empresas, fichaje-digital, precios).
// Renderiza Helmet con OG/Twitter/JSON-LD, nav superior, contenido en `children` y CTA final.
export default function PillarLayout({
  title,
  description,
  canonicalPath,
  jsonLd = null,
  breadcrumbName,
  ctaText = 'Empieza gratis 14 días',
  ctaTo = '/registro',
  children,
}) {
  const url = `https://presenzia.app${canonicalPath}`
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://presenzia.app/landing' },
      { '@type': 'ListItem', position: 2, name: breadcrumbName || title, item: url },
    ],
  }
  return (
    <>
      <Helmet>
        <title>{title} | PresenzIA</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="PresenzIA" />
        <meta property="og:image" content="https://presenzia.app/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://presenzia.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      <div className="min-h-screen bg-white">
        <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
            <Link to="/landing" className="flex items-center gap-2">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold text-gray-900">
                Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span>
              </span>
            </Link>
            <Link to={ctaTo} className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
              {ctaText} <FiArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
          <Link to="/landing" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600">
            <FiArrowLeft className="h-3.5 w-3.5" /> Volver a inicio
          </Link>
        </div>

        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="prose prose-gray prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-blue-600 prose-strong:text-gray-900 prose-li:text-gray-700">
            {children}
          </div>

          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-100 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">¿Listo para empezar?</h3>
            <p className="text-gray-600 mb-5">14 días gratis · Sin tarjeta · Onboarding asistido incluido</p>
            <Link
              to={ctaTo}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 shadow-lg shadow-blue-500/20"
            >
              {ctaText} <FiArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </article>

        <footer className="border-t border-gray-100 py-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-sm text-gray-500 flex flex-wrap items-center justify-between gap-4">
            <span>© 2026 PresenzIA · Hecho en España</span>
            <div className="space-x-4">
              <Link to="/aviso-legal" className="hover:text-blue-600">Aviso legal</Link>
              <Link to="/privacidad" className="hover:text-blue-600">Privacidad</Link>
              <Link to="/cookies" className="hover:text-blue-600">Cookies</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
